import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { CreditCard, FileText, Download, Zap } from "lucide-react";
import toast from "react-hot-toast";
import { BillingModeSettings } from "@/components/admin/BillingModeSettings";
import { UnbilledUsageSummary } from "@/components/admin/UnbilledUsageSummary";
import { UsageLogViewer } from "@/components/admin/UsageLogViewer";
import { UsagePricesEditor } from "@/components/admin/UsagePricesEditor";
import { Badge } from "@/components/ui/badge";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import api from "@/services/api";
import { formatDate } from "@/utils/dateFormat";
import { generateUUID } from "@/utils/transform";

// Status badge for invoices
const InvoiceStatusBadge = ({ status }) => {
  const config = {
    draft: {
      className:
        "bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200",
    },
    sent: {
      className:
        "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200",
    },
    paid: {
      className:
        "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200",
    },
    overdue: {
      className: "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200",
    },
  };
  return (
    <Badge className={config[status]?.className || "bg-gray-100"}>
      {status}
    </Badge>
  );
};

// Credit balance card
const CreditBalanceCard = ({ type, balance, icon: Icon, color }) => (
  <div className="flex items-center justify-between p-4 bg-muted/30 rounded-lg">
    <div className="flex items-center gap-3">
      <div className={`p-2 rounded-md ${color}`}>
        <Icon className="h-4 w-4 text-white" />
      </div>
      <span className="text-sm text-muted-foreground capitalize">{type}</span>
    </div>
    <span className="font-semibold text-foreground">{balance}</span>
  </div>
);

export const OrganisationBillingTab = ({
  organisationId,
  organisationName,
}) => {
  const queryClient = useQueryClient();
  const [activeSubTab, setActiveSubTab] = useState("overview");

  // Fetch organisation billing data
  const { data: billingData, isLoading: billingLoading } = useQuery({
    queryKey: ["admin", "organisation-billing", organisationId],
    queryFn: async () => {
      const response = await api.get(`/admin/organisations/${organisationId}`);
      return response.data?.data;
    },
    enabled: !!organisationId,
  });

  // Fetch organisation invoices
  const { data: invoicesData, isLoading: invoicesLoading } = useQuery({
    queryKey: ["admin", "organisation-invoices", organisationId],
    queryFn: async () => {
      const response = await api.get(
        `/admin/invoices?organisationId=${organisationId}&limit=10`,
      );
      return response.data?.data;
    },
    enabled: !!organisationId,
  });

  // Fetch credits balance
  const { data: creditsData } = useQuery({
    queryKey: ["admin", "organisation-credits", organisationId],
    queryFn: async () => {
      const response = await api.get(
        `/admin/organisations/${organisationId}/credits`,
      );
      return response.data?.data;
    },
    enabled: !!organisationId,
  });

  // Export handlers
  const handleExportInvoices = async (format) => {
    try {
      const response = await api.get(
        `/admin/billing/export/invoices?organisationId=${organisationId}&format=${format}`,
        { responseType: "blob" },
      );

      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `invoices-${organisationName || organisationId}-${new Date().toISOString().split("T")[0]}.${format}`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error("Failed to export invoices");
    }
  };

  const handleExportUsageLog = async (format) => {
    try {
      const response = await api.get(
        `/admin/billing/export/usage-log?organisationId=${organisationId}&format=${format}`,
        { responseType: "blob" },
      );

      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `usage-log-${organisationName || organisationId}-${new Date().toISOString().split("T")[0]}.${format}`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error("Failed to export usage log");
    }
  };

  const billingMode =
    billingData?.billing_mode || billingData?.billingMode || "stripe";
  const invoices = invoicesData?.items || [];
  const isInvoiceBilling = billingMode === "invoice";

  let billingBadgeClass = "bg-purple-100 text-purple-800";
  if (isInvoiceBilling) {
    billingBadgeClass = "bg-blue-100 text-blue-800";
  }

  const planDisplay = billingData?.plan || "Free";

  // Calculate invoice stats
  const invoiceStats = {
    total: invoices.length,
    paid: invoices.filter((i) => i.status === "paid").length,
    outstanding: invoices.filter((i) => ["sent", "overdue"].includes(i.status))
      .length,
    totalAmount: invoices.reduce(
      (sum, i) => sum + parseFloat(i.amount || 0),
      0,
    ),
    outstandingAmount: invoices
      .filter((i) => ["sent", "overdue"].includes(i.status))
      .reduce((sum, i) => sum + parseFloat(i.amount || 0), 0),
  };

  if (billingLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Billing Mode Banner */}
      <Card className="border-border">
        <CardContent className="py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {billingMode === "invoice" && (
                <FileText className="h-5 w-5 text-blue-600" />
              )}
              {billingMode !== "invoice" && (
                <CreditCard className="h-5 w-5 text-purple-600" />
              )}
              <div>
                <p className="font-medium text-foreground">
                  {billingMode === "invoice" && "Invoice Billing"}
                  {billingMode !== "invoice" && "Stripe Billing"}
                </p>
                <p className="text-sm text-muted-foreground">
                  {billingMode === "invoice" &&
                    `Payment terms: Net ${billingData?.payment_terms_days || 14}`}
                  {billingMode !== "invoice" && "Automatic credit card billing"}
                </p>
              </div>
            </div>
            <Badge className={billingBadgeClass}>{planDisplay}</Badge>
          </div>
        </CardContent>
      </Card>

      {/* Sub Tabs */}
      <Tabs value={activeSubTab} onValueChange={setActiveSubTab}>
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="invoices">Invoices</TabsTrigger>
          <TabsTrigger value="usage">Usage Log</TabsTrigger>
          <TabsTrigger value="settings">Settings</TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="mt-6 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Invoice Summary */}
            <Card className="border-border">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  Total Invoiced
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-2xl font-bold text-foreground">
                  ${invoiceStats.totalAmount.toFixed(2)}
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  {invoiceStats.total} invoices
                </p>
              </CardContent>
            </Card>

            <Card className="border-border">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  Outstanding
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-2xl font-bold text-amber-600">
                  ${invoiceStats.outstandingAmount.toFixed(2)}
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  {invoiceStats.outstanding} pending
                </p>
              </CardContent>
            </Card>

            <Card className="border-border">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  Paid
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-2xl font-bold text-green-600">
                  {invoiceStats.paid}
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  invoices paid
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Credits */}
          {creditsData && (
            <Card className="border-border">
              <CardHeader>
                <CardTitle className="text-base">Credit Balances</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <CreditBalanceCard
                  type="SMS"
                  balance={creditsData.sms || 0}
                  icon={Zap}
                  color="bg-blue-500"
                />
                <CreditBalanceCard
                  type="Envelopes"
                  balance={creditsData.envelopes || 0}
                  icon={FileText}
                  color="bg-green-500"
                />
              </CardContent>
            </Card>
          )}

          {/* Unbilled Usage (for invoice customers) */}
          {billingMode === "invoice" && (
            <UnbilledUsageSummary
              organisationId={organisationId}
              organisationName={organisationName}
            />
          )}
        </TabsContent>

        {/* Invoices Tab */}
        <TabsContent value="invoices" className="mt-6">
          <Card className="border-border">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-base">Invoices</CardTitle>
                  <CardDescription>
                    Invoice history for {organisationName}
                  </CardDescription>
                </div>
                <div className="flex items-center gap-2">
                  <Select onValueChange={handleExportInvoices}>
                    <SelectTrigger className="w-32">
                      <Download className="h-4 w-4 mr-2" />
                      <SelectValue placeholder="Export" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="xlsx">Excel (.xlsx)</SelectItem>
                      <SelectItem value="csv">CSV (.csv)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              {invoicesLoading && (
                <div className="p-6 space-y-3">
                  {[...Array(3)].map(() => (
                    <Skeleton key={generateUUID()} className="h-12 w-full" />
                  ))}
                </div>
              )}
              {!invoicesLoading && invoices.length === 0 && (
                <div className="p-8 text-center text-muted-foreground">
                  <FileText className="h-10 w-10 mx-auto mb-3 opacity-50" />
                  <p>No invoices found</p>
                </div>
              )}
              {!invoicesLoading && invoices.length > 0 && (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Invoice</TableHead>
                      <TableHead>Amount</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Due Date</TableHead>
                      <TableHead>Created</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {invoices.map((invoice) => (
                      <TableRow key={invoice.id}>
                        <TableCell className="font-mono text-sm">
                          {invoice.invoiceNumber}
                        </TableCell>
                        <TableCell className="font-medium">
                          ${parseFloat(invoice.amount).toFixed(2)}
                        </TableCell>
                        <TableCell>
                          <InvoiceStatusBadge status={invoice.status} />
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {invoice.dueDate &&
                            formatDate(invoice.dueDate)}
                          {!invoice.dueDate && "-"}
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {formatDate(invoice.createdAt)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Usage Log Tab */}
        <TabsContent value="usage" className="mt-6">
          <div className="flex justify-end mb-4">
            <Select onValueChange={handleExportUsageLog}>
              <SelectTrigger className="w-40">
                <Download className="h-4 w-4 mr-2" />
                <SelectValue placeholder="Export Log" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="xlsx">Excel (.xlsx)</SelectItem>
                <SelectItem value="csv">CSV (.csv)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <UsageLogViewer
            organisationId={organisationId}
            organisationName={organisationName}
          />
        </TabsContent>

        {/* Settings Tab */}
        <TabsContent value="settings" className="mt-6 space-y-6">
          <BillingModeSettings
            organisationId={organisationId}
            organisationName={organisationName}
            onUpdate={() => {
              queryClient.invalidateQueries([
                "admin",
                "organisation-billing",
                organisationId,
              ]);
            }}
          />
          <UsagePricesEditor
            organisationId={organisationId}
            organisationName={organisationName}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default OrganisationBillingTab;
